import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn';

/**
 * Hai khung kéo được (§10.9).
 *
 * Hai điều bắt buộc:
 * - Kẹp tỷ lệ trong khoảng **25–75%**: ngoài khoảng đó một khung hẹp tới mức vô
 *   dụng mà người dùng lại khó kéo trở lại.
 * - Đặt `pointer-events-none` cho khung xem trước **trong lúc đang kéo**: nếu
 *   không, `<iframe>` ăn mất sự kiện `mousemove` và thao tác kéo bị dính
 *   (§18.3 mục 12).
 *
 * Dưới `lg` thì hai khung xếp thành một cột cuộn, không còn kéo được.
 */
export function SplitPane({
  left,
  right,
  initialPercent = 55,
  ariaLabel,
}: {
  left: ReactNode;
  right: ReactNode;
  initialPercent?: number;
  ariaLabel: string;
}) {
  const [percent, setPercent] = useState(initialPercent);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const onPointerDown = useCallback((event: React.PointerEvent) => {
    event.preventDefault();
    setDragging(true);
    const container = containerRef.current;
    if (!container) return;

    const move = (e: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const next = ((e.clientX - rect.left) / rect.width) * 100;
      // Kẹp 25–75%.
      setPercent(Math.min(75, Math.max(25, next)));
    };
    const up = () => {
      setDragging(false);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }, []);

  /** Bàn phím cũng phải kéo được: mỗi lần nhấn dịch 2%. */
  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') setPercent((p) => Math.max(25, p - 2));
    else if (event.key === 'ArrowRight') setPercent((p) => Math.min(75, p + 2));
  };

  return (
    <div
      ref={containerRef}
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto lg:flex-row lg:overflow-hidden"
    >
      <div
        // Xếp dọc (< lg): không cho co dưới chiều cao nội dung, để khung ngoài cuộn thay vì hai khung đè lên nhau.
        className={cn('lg:min-h-0 lg:overflow-hidden', dragging && 'pointer-events-none')}
        style={{ flexBasis: `${percent}%` }}
      >
        {left}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={ariaLabel}
        aria-valuenow={Math.round(percent)}
        aria-valuemin={25}
        aria-valuemax={75}
        tabIndex={0}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        className="group hidden w-1.5 shrink-0 cursor-col-resize items-center justify-center rounded-full lg:flex"
      >
        <span
          className={cn(
            'h-16 w-1.5 rounded-full transition-colors',
            dragging ? 'bg-primary-500' : 'bg-transparent group-hover:bg-strong',
          )}
        />
      </div>

      <div className="flex-1 lg:min-h-0 lg:overflow-hidden">{right}</div>
    </div>
  );
}
